import React, { useMemo, useState } from 'react';
import { Plus, FileText, Calendar, ChevronRight, LogOut, Search } from 'lucide-react';
import { Project } from '../types';
import { useProjects } from '../hooks/useProjects';
import { useAuth } from '../contexts/AuthContext';

interface ProjectListProps {
  projects: Project[];
  onSelectProject: (project: Project) => void;
  onCreateNew: () => void;
  activeTab?: 'assigned' | 'mine';
  onTabChange?: (tab: 'assigned' | 'mine') => void;
}

export const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  onSelectProject,
  onCreateNew,
  activeTab: controlledActiveTab,
  onTabChange,
}) => {
  const { signOut, user } = useAuth();
  const { assignedProjects } = useProjects();
  const [uncontrolledTab, setUncontrolledTab] = useState<'assigned' | 'mine'>(assignedProjects.length > 0 ? 'assigned' : 'mine');
  const activeTab = controlledActiveTab ?? uncontrolledTab;

  const changeTab = (tab: 'assigned' | 'mine') => {
    if (onTabChange) onTabChange(tab);
    setUncontrolledTab(tab);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const [query, setQuery] = useState('');

  const visibleProjects = useMemo(() => {
    const source = activeTab === 'assigned' ? assignedProjects : projects;
    if (!query.trim()) return source;
    const q = query.trim().toLowerCase();
    return source.filter(p => p.name.toLowerCase().startsWith(q));
  }, [activeTab, assignedProjects, projects, query]);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="container mx-auto px-6 py-12">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <p className="text-sm text-zinc-600 mb-1">Manage your annotation projects.</p>
            <h1 className="text-3xl font-semibold text-zinc-900 tracking-tight">My Projects</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-zinc-600">Signed in as</p>
              <p className="text-sm font-medium text-zinc-900">{user?.email}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white ring-1 ring-zinc-300 text-zinc-900 hover:bg-zinc-50 hover:ring-zinc-400 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>

        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="inline-flex bg-white rounded-md ring-1 ring-zinc-300 p-1 w-fit">
            <button
              onClick={() => changeTab('assigned')}
              className={`px-4 py-2 rounded-sm text-sm font-medium ${
                activeTab === 'assigned' ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-50'
              }`}
            >
              Assigned to Me
            </button>
            <button
              onClick={() => changeTab('mine')}
              className={`px-4 py-2 rounded-sm text-sm font-medium ${
                activeTab === 'mine' ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-50'
              }`}
            >
              My Projects
            </button>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects by name"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-zinc-300 text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            />
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* New Project Card: gated by permission (assume authenticated users can create) */}
          {activeTab === 'mine' && (
            <button
              onClick={onCreateNew}
              className="group bg-white rounded-2xl ring-1 ring-zinc-200 p-8 hover:ring-zinc-300 transition-all duration-200 text-center min-h-[200px] flex flex-col items-center justify-center"
            >
              <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-zinc-200 transition-colors">
                <Plus className="w-8 h-8 text-zinc-900" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-900 mb-2">
                New Project
              </h3>
              <p className="text-zinc-600">
                Create a new annotation project
              </p>
            </button>
          )}

          {/* Existing Projects */}
          {visibleProjects.map((project) => (
            <button
              key={project.id}
              onClick={() => onSelectProject({ ...project, role: activeTab === 'assigned' ? 'attemptor' : project.role })}
              className="group bg-white rounded-2xl shadow-sm ring-1 ring-zinc-200 p-6 hover:ring-zinc-300 transition-all duration-200 text-left"
            >
              <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'color-mix(in oklab, var(--accent) 12%, white)' }}>
                  <FileText className="w-6 h-6" style={{ color: 'var(--accent)' }} />
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-300 group-hover:text-zinc-600 transition-colors" />
              </div>
              
              <h3 className="text-lg font-semibold text-zinc-900 mb-2 group-hover:text-zinc-800 transition-colors">
                {project.name}
              </h3>
              
              <div className="flex items-center text-xs text-zinc-500">
                <Calendar className="w-4 h-4 mr-1" />
                Updated {formatDate(project.updatedAt)}
              </div>
            </button>
          ))}
        </div>

        {visibleProjects.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-12 h-12 text-zinc-400" />
            </div>
            <h3 className="text-xl font-semibold text-zinc-900 mb-2">
              {query ? 'No matching projects' : (activeTab === 'assigned' ? 'No assigned projects yet' : 'No projects yet')}
            </h3>
            <p className="text-zinc-600 mb-8">
              {query ? 'Try a different search term.' : (activeTab === 'assigned' ? 'You will see projects here when an admin assigns you.' : 'Create your first annotation project to get started')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};